import { Dispatch, SetStateAction, useCallback, useEffect, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';

// For tree headings debounce
const DEFAULT_DELAY = 300; // 300ms after the last change
const DEFAULT_MAX_WAIT = 2000; // force update at least every 2s

/**
 * Keeps a mutable ref **always-in-sync** with React state,
 * but debounces the actual state-setter to avoid re-renders.
 *
 * @returns
 *   ref.current  – latest value (instant update, great for autosave etc.)
 *   onUpdate(v)  – call this from TipTap plugins → pushes value into the hook
 */
export const useDebouncedRef = <T>(
  setState: Dispatch<SetStateAction<T>>,
  initial: T,
  delay = DEFAULT_DELAY,
  maxWait = DEFAULT_MAX_WAIT
) => {
  const ref = useRef<T>(initial);

  const debouncedSet = useDebouncedCallback(setState, delay, { maxWait });

  const onUpdate = useCallback(
    (next: T) => {
      ref.current = next; // ←  instant write for imperative code
      debouncedSet(next); // ←  coalesced React render
    },
    [debouncedSet]
  );

  // cancel debounce timer on unmount
  useEffect(() => () => debouncedSet.cancel(), [debouncedSet]);

  return { ref, onUpdate }; // {ref.current , onUpdate(..)}
};
